import React, { useState, useEffect } from 'react';
import { GraduationCap, Calculator, DollarSign, Percent, Calendar, Info } from 'lucide-react';
import { motion } from 'framer-motion';

interface Loan {
  id: string;
  name: string;
  balance: number;
  interestRate: number;
  minimumPayment: number;
  type: 'federal' | 'private';
}

interface StudentLoansProps {
  onUpdateMonthlyPayment: (amount: number) => void;
}

export const StudentLoans: React.FC<StudentLoansProps> = ({ onUpdateMonthlyPayment }) => {
  const [loans, setLoans] = useState<Loan[]>([]);
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorValues, setCalculatorValues] = useState({
    loanAmount: 10000,
    interestRate: 5.5,
    loanTerm: 10,
  });

  const addLoan = () => {
    const newLoan: Loan = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      balance: 0,
      interestRate: 0,
      minimumPayment: 0,
      type: 'federal'
    };
    setLoans([...loans, newLoan]);
  };

  const updateLoan = (id: string, field: keyof Loan, value: string | number) => {
    setLoans(loans.map(loan => {
      if (loan.id === id) {
        return { ...loan, [field]: value };
      }
      return loan;
    }));
  };

  const removeLoan = (id: string) => {
    setLoans(loans.filter(loan => loan.id !== id));
  };

  const calculateMonthlyPayment = (amount: number, rate: number, years: number) => {
    const monthlyRate = (rate / 100) / 12;
    const numberOfPayments = years * 12;
    const monthlyPayment = amount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    return monthlyPayment;
  };

  const totalMonthlyPayment = loans.reduce((sum, loan) => sum + loan.minimumPayment, 0);
  const totalBalance = loans.reduce((sum, loan) => sum + loan.balance, 0);
  const averageInterestRate = loans.length > 0 
    ? loans.reduce((sum, loan) => sum + (loan.interestRate * (loan.balance / totalBalance)), 0)
    : 0;

  useEffect(() => {
    onUpdateMonthlyPayment(totalMonthlyPayment);
  }, [totalMonthlyPayment, onUpdateMonthlyPayment]);

  return (
    <div className="glass-card p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-500/10">
            <GraduationCap className="w-6 h-6 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-semibold">Student Loans</h2>
        </div>
        <div className="flex gap-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCalculator(!showCalculator)}
            className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
          >
            <Calculator className="w-4 h-4" />
            Payment Calculator
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={addLoan}
            className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors"
          >
            Add Loan
          </motion.button>
        </div>
      </div>

      {showCalculator && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="glass-card p-6 mb-6 border border-emerald-500/20"
        >
          <h3 className="text-lg font-semibold mb-4">Loan Payment Calculator</h3>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Loan Amount</label>
              <div className="relative">
                <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={calculatorValues.loanAmount}
                  onChange={(e) => setCalculatorValues({
                    ...calculatorValues,
                    loanAmount: Number(e.target.value)
                  })}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Interest Rate (%)</label>
              <div className="relative">
                <Percent className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={calculatorValues.interestRate}
                  onChange={(e) => setCalculatorValues({
                    ...calculatorValues,
                    interestRate: Number(e.target.value)
                  })}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                  step="0.1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Loan Term (Years)</label>
              <div className="relative">
                <Calendar className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={calculatorValues.loanTerm}
                  onChange={(e) => setCalculatorValues({
                    ...calculatorValues,
                    loanTerm: Number(e.target.value)
                  })}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="1"
                />
              </div>
            </div>
          </div>
          <div className="bg-emerald-500/5 p-4 rounded-lg">
            <p className="text-emerald-500 font-semibold">
              Estimated Monthly Payment: ${calculateMonthlyPayment(
                calculatorValues.loanAmount,
                calculatorValues.interestRate,
                calculatorValues.loanTerm
              ).toFixed(2)}
            </p>
          </div>
        </motion.div>
      )}

      <div className="space-y-4">
        {loans.map((loan) => (
          <div key={loan.id} className="glass-card p-4 hover:bg-slate-800/30 transition-colors duration-200">
            <div className="grid md:grid-cols-6 gap-4">
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Loan Name</label>
                <input
                  type="text"
                  value={loan.name}
                  onChange={(e) => updateLoan(loan.id, 'name', e.target.value)}
                  placeholder="e.g., Federal Subsidized"
                  className="glass-input px-4 py-2 w-full rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Balance</label>
                <div className="relative">
                  <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="number"
                    value={loan.balance}
                    onChange={(e) => updateLoan(loan.id, 'balance', Number(e.target.value))}
                    className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                    min="0"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Interest Rate (%)</label>
                <div className="relative">
                  <Percent className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="number"
                    value={loan.interestRate}
                    onChange={(e) => updateLoan(loan.id, 'interestRate', Number(e.target.value))}
                    className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Monthly Payment</label>
                <div className="relative">
                  <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="number"
                    value={loan.minimumPayment}
                    onChange={(e) => updateLoan(loan.id, 'minimumPayment', Number(e.target.value))}
                    className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                    min="0"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm text-slate-400">Type</label>
                <select
                  value={loan.type}
                  onChange={(e) => updateLoan(loan.id, 'type', e.target.value)}
                  className="glass-input px-4 py-2 w-full rounded-lg appearance-none"
                >
                  <option value="federal">Federal</option>
                  <option value="private">Private</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={() => removeLoan(loan.id)}
                  className="px-4 py-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors w-full"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))}

        {loans.length > 0 && (
          <div className="glass-card p-6 border border-emerald-500/20">
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-slate-400 mb-1">Total Balance</p>
                <p className="text-2xl font-semibold text-emerald-500">
                  ${totalBalance.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-1">Monthly Payment</p>
                <p className="text-2xl font-semibold text-emerald-500">
                  ${totalMonthlyPayment.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400 mb-1">Avg. Interest Rate</p>
                <p className="text-2xl font-semibold text-emerald-500">
                  {averageInterestRate.toFixed(2)}%
                </p>
              </div>
            </div>
          </div>
        )}

        {loans.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <GraduationCap className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No student loans added yet. Click "Add Loan" to get started.</p>
          </div>
        )}
      </div>

      <div className="mt-6 p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-emerald-500 mt-1 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-emerald-500 mb-2">Student Loan Tips</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>• Consider income-driven repayment plans for federal loans</li>
              <li>• Look into loan forgiveness programs if you work in public service</li>
              <li>• Pay more than the minimum when possible to reduce interest costs</li>
              <li>• Keep track of your loan servicers and payment due dates</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};